/**
 * Organization member & invitation service functions.
 */
import { apiClient } from '@/lib/api-client';

/**
 * The roles a person can be given. `owner` is absent on purpose: it is not
 * assignable, it moves through transfer-ownership, and the server refuses it
 * here (`ASSIGNABLE_MEMBER_ROLES`).
 */
export type AssignableRole = 'admin' | 'member';

export async function fetchUserMembership(userId: string) {
  return apiClient.get<{ organizationId: string | null; role: 'owner' | 'admin' | 'member' | null } | null>('/organizations/members/membership', { userId });
}

export async function fetchOrgMembers(orgId: string) {
  return apiClient.get<any[]>('/organizations/members', { orgId });
}

export async function fetchProfilesByIds(userIds: string[]) {
  return apiClient.post<any[]>('/organizations/members/profiles', { userIds });
}

export async function deleteOrgMember(memberId: string, orgId: string) {
  await apiClient.delete('/organizations/members/' + memberId + '?orgId=' + orgId);
}

export async function fetchOrgInvitations(orgId: string) {
  return apiClient.get<any[]>('/organizations/invitations', { orgId });
}

/**
 * `role` is sent rather than hardcoded to 'member'. It was pinned, so the only
 * way to have an admin in an organisation was to call the API by hand, even
 * though `InviteMemberDto` has accepted 'admin' since the role split.
 */
export async function insertOrgInvitation(invitation: {
  organizationId: string;
  email: string;
  invitedBy: string;
  role?: AssignableRole;
}) {
  await apiClient.post('/organizations/invite', {
    ...invitation,
    role: invitation.role ?? 'member',
  });
}

export async function deleteOrgInvitation(invitationId: string, orgId: string) {
  await apiClient.delete('/organizations/invitations/' + invitationId + '?orgId=' + orgId);
}

export async function updateOrgMemberRole(memberId: string, role: AssignableRole) {
  await apiClient.patch('/organizations/members/' + memberId, { role });
}

/**
 * The public by-token lookup. This is an unauthenticated route, so the server
 * returns only what the accept-invite page renders. It deliberately does not
 * echo the token back, and does not expose `invitedBy`.
 */
export interface InvitationByTokenResponse {
  id: string;
  organizationId: string;
  email: string;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  /** Embedded by the API so anonymous invitees can show "Join <org>". */
  organizationName: string | null;
}

export async function fetchInvitationByToken(token: string) {
  return apiClient.get<InvitationByTokenResponse>('/organizations/invitations/by-token', { token });
}

export async function markInvitationAccepted(invitationId: string) {
  await apiClient.patch('/organizations/invitations/' + invitationId, { accepted: true });
}
